import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { CheckCircle2, Circle, Download, FileDown, Loader2, RotateCcw, ShieldCheck, Truck } from "lucide-react";
import { toast } from "sonner";
import Nav from "@/components/Nav";
import { api, BACKEND, errMsg, inr } from "@/lib/api";

const SENT = new Set(["queued", "sent", "accepted"]);
function receiptStatus(o) {
  const wa = o.receipt_sent, em = o.receipt_email;
  if (!wa && !em) return "Sending your receipt…";
  const parts = [];
  if (em && SENT.has(em.status)) parts.push(`emailed to ${em.to}`);
  if (wa && SENT.has(wa.status)) parts.push(`sent on WhatsApp to ${wa.to}`);
  if (parts.length) return `Receipt ${parts.join(" and ")}.`;
  return "Your receipt is ready — download it here anytime.";
}

export const reorderPath = (o) => (o.kind === "caricature" ? (o.format === "print" ? `/caricature/${o.caricature_id}?reorder=${o.id}` : null) : `/books/${o.book_id}/checkout?reorder=${o.id}`);

export const STATUS_LABEL = {
  created: "Awaiting payment", paid: "Payment received", pdf_ready: "Print files ready", sent_to_printer: "Sent to printer",
  printing: "Printing", binding: "Binding", quality_check: "Quality check", shipped: "Shipped", delivered: "Delivered",
  fulfilment_error: "Needs attention",
};

export default function OrderPage() {
  const { orderId } = useParams();
  const [o, setO] = useState(null);
  useEffect(() => {
    const load = () => api.get(`/orders/${orderId}`).then((r) => setO(r.data));
    load();
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [orderId]);
  if (!o) return null;
  const flow = o.status_flow;
  const idx = flow.indexOf(o.status);
  const at = Object.fromEntries(o.history.map((h) => [h.status, h.at]));
  const advance = async () => {
    try { const r = await api.post(`/orders/${orderId}/simulate-advance`); setO(r.data); } catch (e) { toast.error(errMsg(e)); }
  };
  const eta = new Date(new Date(o.created_at).getTime() + 14 * 864e5).toLocaleDateString("en-IN", { day: "numeric", month: "long" });
  const caric = o.kind === "caricature";

  return (
    <div className="min-h-screen">
      <Nav />
      <main className="mx-auto max-w-4xl px-5 py-12 sm:px-8">
        <p className="overline">Order #{o.id.slice(-8).toUpperCase()}</p>
        <h1 className="mt-2 font-serif text-4xl tracking-tight">{o.book_title}</h1>
        <div className="mt-3 flex flex-wrap items-center gap-3 text-sm text-[#5E5868]">
          <span data-testid="order-timeline-status-badge" className="rounded-full bg-[#F0F5F1] px-3 py-1 font-medium text-[#4F7A5C]">{STATUS_LABEL[o.status] || o.status}</span>
          <span>{inr(o.pricing.total)} · {caric ? (o.format === "digital" ? "Digital poster" : "Printed A3 poster") : `${o.size} · ${o.pages} pages`}</span>
          {o.pricing.promo && <span className="text-[#4F7A5C]" data-testid="order-promo">Promo {o.pricing.promo} · −{inr(o.pricing.discount)}</span>}
          {o.gift_wrap && <span className="rounded-full bg-[#FCE4EC] px-3 py-1 text-xs font-medium text-[#C2185B]" data-testid="order-giftwrap-badge">Gift wrapped{o.gift_message ? " · with card" : ""}</span>}
          {o.status !== "created" && reorderPath(o) && (
            <Link to={reorderPath(o)} data-testid="order-reorder-btn" className="btn-ghost !py-1.5 !text-xs"><RotateCcw className="h-3.5 w-3.5" />Reorder</Link>
          )}
        </div>
        {o.status !== "created" && (
          <div className="mt-5 flex flex-wrap items-center gap-3 text-sm" data-testid="order-receipt-row">
            <a href={`${BACKEND}/api/orders/${o.id}/receipt`} target="_blank" rel="noreferrer" data-testid="order-receipt-btn" className="btn-ghost !py-2 !text-xs"><FileDown className="h-3.5 w-3.5" />GST receipt (PDF)</a>
            <span className="text-xs text-[#8C8498]" data-testid="order-receipt-status">{receiptStatus(o)}</span>
          </div>
        )}
        {caric && o.format === "digital" ? (
          <div className="paper mt-10 rounded-lg p-6" data-testid="order-digital-panel">
            <p className="font-serif text-2xl">Your poster is ready.</p>
            <p className="mt-1 text-sm text-[#5E5868]">Full resolution, no watermark. Download it as many times as you like.</p>
            <div className="mt-5 flex flex-wrap gap-3">
              <a data-testid="order-download-poster-btn" href={`${BACKEND}/api/caricatures/${o.caricature_id}/download`} className="btn-primary"><Download className="h-4 w-4" />Download poster</a>
              <Link to={`/caricature/${o.caricature_id}`} data-testid="order-view-caricature-link" className="btn-ghost">View caricature</Link>
            </div>
          </div>
        ) : (<>
        <p className="mt-6 text-sm text-[#5E5868]" data-testid="order-eta">
          Target delivery around <strong className="text-[#26232E]">{eta}</strong> — about 2 weeks from your order. It's a target, not a guarantee; we'll keep you posted.
          {o.domestic === false && <span className="text-[#D97706]"> This order was routed to an international print partner, so delivery may take longer.</span>}
        </p>

        <ol className="mt-10 space-y-0" data-testid="order-timeline">
          {flow.map((s, k) => {
            const done = k <= idx;
            return (
              <li key={s} className="relative flex gap-4 pb-7 last:pb-0" data-testid={`order-step-${s}`}>
                {k < flow.length - 1 && <span className={`absolute left-[11px] top-6 h-full w-px ${k < idx ? "bg-[#789A82]" : "bg-[#E8E2D9]"}`} />}
                {done ? <CheckCircle2 className="relative h-6 w-6 shrink-0 bg-[#FDFBF7] text-[#789A82]" /> : k === idx + 1 && idx < 2 ? <Loader2 className="h-6 w-6 shrink-0 animate-spin text-[#3B5BDB]" /> : <Circle className="relative h-6 w-6 shrink-0 bg-[#FDFBF7] text-[#E8E2D9]" />}
                <div>
                  <p className={`font-medium ${done ? "" : "text-[#8C8498]"}`}>{STATUS_LABEL[s]}</p>
                  {at[s] && <p className="text-xs text-[#8C8498]">{new Date(at[s]).toLocaleString("en-IN")}</p>}
                  {s === "shipped" && o.tracking && (
                    <div className="mt-1">
                      <p className="font-mono text-xs" data-testid="order-tracking-awb">{o.tracking.carrier} · AWB {o.tracking.awb}</p>
                      {o.tracking.url && <a href={o.tracking.url} target="_blank" rel="noreferrer" data-testid="order-track-btn" className="btn-primary mt-2 !py-1.5 !text-xs"><Truck className="h-3.5 w-3.5" />Track parcel live</a>}
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
        {o.status === "fulfilment_error" && <p className="mt-6 rounded-md bg-rose-50 p-3 text-sm text-rose-700">We hit a snag preparing your print files. Our team has been notified.</p>}

        <div className="mt-12 grid gap-4 sm:grid-cols-2">
          <div className="paper rounded-lg p-5 text-sm">
            <p className="font-semibold">Print partner</p>
            <p className="mt-1 text-[#5E5868]" data-testid="order-provider">{o.provider_info?.name}{o.provider_order_id ? ` · ${o.provider_order_id}` : ""}</p>
            {o.provider_info?.mocked && <p className="mt-1 text-xs text-amber-700">MOCKED provider (no live API key yet)</p>}
            {o.pdf_paths?.interior && (
              <div className="mt-4 flex flex-wrap gap-2">
                <a data-testid="order-pdf-interior-link" className="btn-ghost !py-1.5 !text-xs" href={`${BACKEND}/api/orders/${o.id}/pdf/interior`} target="_blank" rel="noreferrer"><FileDown className="h-3.5 w-3.5" />Interior PDF</a>
                <a data-testid="order-pdf-cover-link" className="btn-ghost !py-1.5 !text-xs" href={`${BACKEND}/api/orders/${o.id}/pdf/cover`} target="_blank" rel="noreferrer"><FileDown className="h-3.5 w-3.5" />Cover PDF</a>
              </div>
            )}
            {o.pdf_paths?.poster && (
              <div className="mt-4 flex flex-wrap gap-2">
                <a data-testid="order-pdf-poster-link" className="btn-ghost !py-1.5 !text-xs" href={`${BACKEND}/api/orders/${o.id}/pdf/poster`} target="_blank" rel="noreferrer"><FileDown className="h-3.5 w-3.5" />Poster PDF (A3 + bleed)</a>
                <a data-testid="order-download-poster-btn" className="btn-ghost !py-1.5 !text-xs" href={`${BACKEND}/api/caricatures/${o.caricature_id}/download`}><Download className="h-3.5 w-3.5" />Digital file</a>
              </div>
            )}
          </div>
          <div className="paper rounded-lg p-5 text-sm">
            <p className="flex items-center gap-2 font-semibold"><ShieldCheck className="h-4 w-4 text-[#789A82]" />Photo privacy</p>
            <p className="mt-1 text-[#5E5868]" data-testid="order-privacy-note">
              {o.purge_after ? `Your photos will be automatically deleted on ${new Date(o.purge_after).toLocaleDateString("en-IN")}.` : "Your photos are automatically deleted 30 days after delivery."}
            </p>
          </div>
        </div>
        {idx >= 2 && o.status !== "delivered" && (
          <button data-testid="order-simulate-advance-btn" onClick={advance} className="btn-ghost mt-8 !text-xs">Demo: simulate next print-partner update</button>
        )}
        </>)}
      </main>
    </div>
  );
}
